import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building,
  Bed,
  User,
  FileText,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Loader,
  Upload,
  X,
  MapPin,
  Phone,
  Mail,
} from 'lucide-react';
import { getHospitalApplication, updateHospitalProfile, uploadDocument } from '../../services/hospitalService';

// Admin Hospital Edit – reuse Registration flow, but prefilled and without password/referral
const HospitalEdit = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');

  // Data state
  const [docId, setDocId] = useState(null); // Firestore document id
  const [existingDocuments, setExistingDocuments] = useState({});
  const [formData, setFormData] = useState({
    hospitalName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    numberOfBeds: '',
    facilities: [],
    specialities: [],
    // Registration
    registrationNumber: '',
    registrationValidityDate: '',
    // Pharmacy / quality
    pharmacyType: '',
    dlNumber: '',
    hasNABH: false,
    hasQualityCouncil: false,
    // Contacts
    mdName: '',
    mdContact: '',
    mdEmail: '',
    mdAsRepresentative: false,
    representativeName: '',
    designation: '',
    representativeEmail: '',
    representativePhone: '',
    // Account & Bank (accountEmail read-only)
    accountEmail: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
  });

  // New uploads holder (optional replacements)
  const [documents, setDocuments] = useState({
    registrationCert: null,
    nabhCert: null,
    qualityCouncilCert: null,
    pharmacyCert: null,
    facilitiesPhotos: [], // multiple add-on
    medicalEstablishmentCert: null,
    authorizationLetter: null,
    cancelledCheque: null,
    panDocument: null,
    gstDocument: null,
    roomTariff: null,
    procedureTariff: null,
  });

  // Constants
  const steps = useMemo(() => ([
    { id: 1, title: 'Basic Information', icon: Building },
    { id: 2, title: 'Hospital Details', icon: Bed },
    { id: 3, title: 'Contact Information', icon: User },
    { id: 4, title: 'Documents', icon: FileText },
    { id: 5, title: 'Account & Banking', icon: CreditCard },
    { id: 6, title: 'Review & Save', icon: CheckCircle }
  ]), []);

  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  const facilitiesList = [
    'Emergency Services', 'ICU', 'Operation Theatre', 'Laboratory', 'Radiology',
    'Pharmacy', 'Blood Bank', 'Dialysis', 'Physiotherapy', 'Ambulance Service',
    'Cafeteria', 'Parking', '24/7 Services', 'Oxygen Plant', 'CCTV Surveillance'
  ];

  const specialitiesList = [
    'Cardiology', 'Orthopedics', 'Neurology', 'Pediatrics', 'Gynecology',
    'Oncology', 'Dermatology', 'ENT', 'Ophthalmology', 'Psychiatry',
    'General Surgery', 'Anesthesiology', 'Radiology', 'Pathology', 'Emergency Medicine',
    'Internal Medicine', 'Urology', 'Gastroenterology', 'Pulmonology', 'Nephrology'
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getHospitalApplication(applicationId);
        if (!res.success) throw new Error(res.error || 'Not found');
        const app = res.application;
        setDocId(app.id);
        setExistingDocuments(app.documents || {});

        // Map Firestore structure to formData
        const rep = app.representative || {};
        const md = app.managingDirector || {};
        const mdAsRep = !!rep.isMD;
        setFormData({
          hospitalName: app.hospitalName || '',
          address: app.address || '',
          city: app.city || '',
          state: app.state || '',
          pincode: app.pincode || '',
          phone: app.phone || '',
          email: app.email || '',
          website: app.website || '',
          numberOfBeds: String(app.numberOfBeds || ''),
          facilities: Array.isArray(app.facilities) ? app.facilities : [],
          specialities: Array.isArray(app.specialities) ? app.specialities : [],
          registrationNumber: app.registrationNumber || '',
          registrationValidityDate: app.registrationValidityDate || '',
          pharmacyType: app.pharmacyType || '',
          dlNumber: app.dlNumber || '',
          hasNABH: !!app.hasNABH,
          hasQualityCouncil: !!app.hasQualityCouncil,
          mdName: md.name || '',
          mdContact: md.contact || '',
          mdEmail: md.email || '',
          mdAsRepresentative: mdAsRep,
          representativeName: mdAsRep ? md.name || '' : rep.name || '',
          designation: mdAsRep ? 'Managing Director' : rep.designation || '',
          representativeEmail: mdAsRep ? md.email || '' : rep.email || '',
          representativePhone: mdAsRep ? md.contact || '' : rep.phone || '',
          accountEmail: app.accountEmail || '',
          bankName: app.bankDetails?.bankName || '',
          accountNumber: app.bankDetails?.accountNumber || '',
          ifscCode: app.bankDetails?.ifscCode || '',
          accountHolderName: app.bankDetails?.accountHolderName || '',
        });
      } catch (e) {
        setError(e.message || 'Failed to load application');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [applicationId]);

  // Handlers
  const setField = (name, value) => {
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // If toggling MD as representative, sync fields
      if (name === 'mdAsRepresentative') {
        if (value) {
          updated.representativeName = updated.mdName;
          updated.representativeEmail = updated.mdEmail;
          updated.representativePhone = updated.mdContact;
          updated.designation = 'Managing Director';
        }
      }
      if (updated.mdAsRepresentative) {
        if (name === 'mdName') updated.representativeName = value;
        if (name === 'mdEmail') updated.representativeEmail = value;
        if (name === 'mdContact') updated.representativePhone = value;
      }
    return updated;
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const handleMultiToggle = (field, option) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(option) ? prev[field].filter(o => o !== option) : [...prev[field], option]
    }));
  };

  const handleFileUpload = (docKey, file, isMultiple = false) => {
    if (!file) return;
    if (isMultiple) {
      setDocuments(prev => ({ ...prev, [docKey]: [...(prev[docKey] || []), file] }));
    } else {
      setDocuments(prev => ({ ...prev, [docKey]: file }));
    }
  };

  const documentConfig = [
    { key: 'registrationCert', label: 'Hospital Registration Certificate' },
    { key: 'nabhCert', label: 'NABH Certificate' },
    { key: 'qualityCouncilCert', label: 'Quality Council of India Certificate' },
    { key: 'pharmacyCert', label: 'Pharmacy Certificate' },
    { key: 'facilitiesPhotos', label: 'Facilities Photos (add more)', multiple: true },
    { key: 'medicalEstablishmentCert', label: 'Medical Establishment Act Certificate' },
    { key: 'authorizationLetter', label: 'Authorization Letter' },
    { key: 'cancelledCheque', label: 'Cancelled Cheque' },
    { key: 'panDocument', label: 'Hospital PAN Document' },
    { key: 'gstDocument', label: 'GST Certificate' },
    { key: 'roomTariff', label: 'Hospital Room Tariff Document' },
    { key: 'procedureTariff', label: 'Procedure Tariff Document' },
  ];

  const validate = (step) => {
    // Minimal validation suitable for edit
    switch (step) {
      case 1:
        return !!(formData.hospitalName && formData.address && formData.city && formData.state && formData.pincode && formData.phone && formData.email);
      case 2:
        return !!(formData.numberOfBeds && formData.facilities.length && formData.specialities.length);
      case 3:
        return !!(formData.mdName && formData.mdContact && (formData.mdAsRepresentative || (formData.representativeName && formData.designation && formData.representativeEmail && formData.representativePhone)));
      case 4:
        return true; // documents are optional in edit
      case 5:
        return !!(formData.accountEmail && formData.bankName && formData.accountNumber && formData.ifscCode && formData.accountHolderName);
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validate(currentStep)) setCurrentStep(s => Math.min(6, s + 1));
    else setError('Please complete required fields in this step.');
  };
  const prevStep = () => setCurrentStep(s => Math.max(1, s - 1));

  const saveChanges = async () => {
    setError('');
    if (!validate(5)) {
      setError('Please complete all required details before saving.');
      setCurrentStep(1);
      return;
    }
    setSaving(true);
    try {
      // Upload any newly provided documents
      const uploads = [];
      const newDocEntries = {};

      // Single-doc keys
      const singleKeys = Object.keys(documents).filter(k => k !== 'facilitiesPhotos');
      for (const key of singleKeys) {
        const file = documents[key];
        if (file) {
          uploads.push(
            uploadDocument(file, key, formData.hospitalName).then(url => {
              newDocEntries[key] = {
                url,
                fileName: file.name,
                size: file.size,
                uploadedAt: new Date(),
              };
            })
          );
        }
      }

      // Facilities photos: append with next indices
      if (Array.isArray(documents.facilitiesPhotos) && documents.facilitiesPhotos.length) {
        const existingKeys = Object.keys(existingDocuments || {}).filter(k => k.startsWith('facilitiesPhoto_'));
        let nextIndex = existingKeys.reduce((max, k) => {
          const n = parseInt(k.split('facilitiesPhoto_')[1] || '0', 10);
          return isNaN(n) ? max : Math.max(max, n);
        }, -1) + 1;

        for (const file of documents.facilitiesPhotos) {
          const thisKey = `facilitiesPhoto_${nextIndex}`;
          nextIndex += 1;
          uploads.push(
            uploadDocument(file, thisKey, formData.hospitalName).then(url => {
              newDocEntries[thisKey] = {
                url,
                fileName: file.name,
                size: file.size,
                uploadedAt: new Date(),
              };
            })
          );
        }
      }

      await Promise.all(uploads);

      // Merge documents: overwrite single keys, append new facility photos
      const mergedDocuments = { ...(existingDocuments || {}), ...newDocEntries };

      // Build update payload (mirror registerHospital fields)
      const updatePayload = {
        hospitalName: formData.hospitalName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        phone: formData.phone,
        email: formData.email,
        website: formData.website || '',
        numberOfBeds: parseInt(formData.numberOfBeds, 10) || 0,
        facilities: formData.facilities,
        specialities: formData.specialities,
        registrationNumber: formData.registrationNumber || '',
        registrationValidityDate: formData.registrationValidityDate || '',
        pharmacyType: formData.pharmacyType || '',
        dlNumber: formData.dlNumber || '',
        hasNABH: !!formData.hasNABH,
        hasQualityCouncil: !!formData.hasQualityCouncil,
        representative: {
          name: formData.mdAsRepresentative ? formData.mdName : formData.representativeName,
          designation: formData.mdAsRepresentative ? 'Managing Director' : formData.designation,
          email: formData.mdAsRepresentative ? formData.mdEmail : formData.representativeEmail,
          phone: formData.mdAsRepresentative ? formData.mdContact : formData.representativePhone,
          isMD: !!formData.mdAsRepresentative,
        },
        managingDirector: {
          name: formData.mdName,
          contact: formData.mdContact,
          email: formData.mdEmail || '',
        },
        accountEmail: formData.accountEmail, // kept read-only in UI, but allow persisting if changed later
        bankDetails: {
          bankName: formData.bankName,
          accountNumber: formData.accountNumber,
          ifscCode: formData.ifscCode,
          accountHolderName: formData.accountHolderName,
        },
        documents: mergedDocuments,
      };

      const result = await updateHospitalProfile(docId, updatePayload);
      if (!result.success) throw new Error(result.error || 'Update failed');

      // Navigate back to details
      navigate(`/admin/hospital-details/${applicationId}`);
    } catch (e) {
      setError(e.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Render helpers
  const renderFormField = (name, label, type = 'text', required = true, placeholder = '', options = {}) => (
    <div>
      <label className="form-label">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'select' ? (
        <select
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`input-field ${options.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={options.disabled}
        >
          <option value="">{placeholder}</option>
          {(options.list || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`input-field ${options.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          rows={options.rows || 3}
          disabled={options.disabled}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`input-field ${options.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          min={options.min}
          max={options.max}
          step={options.step}
          disabled={options.disabled}
        />
      )}
    </div>
  );

  const renderMultiSelect = (field, label, options) => (
    <div>
      <label className="form-label">{label} <span className="text-red-500">*</span></label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto">
        {options.map(option => (
          <label key={option} className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData[field].includes(option)}
              onChange={() => handleMultiToggle(field, option)}
              className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderDocumentUpload = (doc) => (
    <div key={doc.key} className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-800">{doc.label}</h4>
        {documents[doc.key] && !doc.multiple && (
          <button onClick={() => setDocuments(prev => ({ ...prev, [doc.key]: null }))} className="text-red-500 hover:text-red-700 p-1">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Existing doc preview */}
      {!doc.multiple && existingDocuments[doc.key] && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
          <div className="flex items-center space-x-2 text-sm">
            <FileText className="h-4 w-4 text-gray-600" />
            <span className="text-gray-700">Existing: {existingDocuments[doc.key].fileName}</span>
          </div>
        </div>
      )}

      {/* Upload control */}
      {!doc.multiple ? (
        !documents[doc.key] ? (
          <div className="dropzone p-6 text-center">
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-2">Click to upload a replacement file (optional)</p>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id={`up_${doc.key}`}
                   onChange={(e) => handleFileUpload(doc.key, e.target.files?.[0])} />
            <label htmlFor={`up_${doc.key}`} className="btn-primary mt-2 cursor-pointer">Choose File</label>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-800">{documents[doc.key].name}</span>
              </div>
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
        )
      ) : (
        <div>
          <div className="dropzone p-6 text-center">
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-2">Add more photos (optional)</p>
            <input type="file" accept=".jpg,.jpeg,.png" multiple className="hidden" id={`up_${doc.key}`}
                   onChange={(e) => Array.from(e.target.files || []).forEach(f => handleFileUpload(doc.key, f, true))} />
            <label htmlFor={`up_${doc.key}`} className="btn-primary mt-2 cursor-pointer">Choose Photos</label>
          </div>
          {documents[doc.key] && documents[doc.key].length > 0 && (
            <p className="text-sm text-green-700 mt-2">{documents[doc.key].length} new photo(s) selected</p>
          )}
        </div>
      )}
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField('hospitalName', 'Hospital Name')}
              {renderFormField('phone', 'Phone Number', 'tel')}
            </div>
            {renderFormField('address', 'Address', 'textarea', true, 'Enter complete address', { rows: 3 })}
            <div className="grid md:grid-cols-3 gap-6">
              {renderFormField('city', 'City')}
              {renderFormField('state', 'State', 'select', true, 'Select State', { list: states })}
              {renderFormField('pincode', 'Pincode')}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField('email', 'Email Address', 'email')}
              {renderFormField('website', 'Website', 'url', false, 'https://www.hospital.com')}
            </div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">Registration Details</h4>
              <div className="grid md:grid-cols-2 gap-6">
                {renderFormField('registrationNumber', 'Registration Number')}
                {renderFormField('registrationValidityDate', 'Registration Validity Date', 'date')}
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            {renderFormField('numberOfBeds', 'Number of Beds', 'number', true, '', { min: 1 })}
            {renderMultiSelect('facilities', 'Facilities Available', facilitiesList)}
            {renderMultiSelect('specialities', 'Medical Specialities', specialitiesList)}
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-2">Pharmacy Information</h4>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Pharmacy Type <span className="text-red-500">*</span></label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="radio" name="pharmacyType" value="inhouse" checked={formData.pharmacyType === 'inhouse'} onChange={handleInputChange} className="text-purple-500 focus:ring-purple-500" />
                      <span className="text-sm text-gray-700">In-house Pharmacy</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="radio" name="pharmacyType" value="outsourced" checked={formData.pharmacyType === 'outsourced'} onChange={handleInputChange} className="text-purple-500 focus:ring-purple-500" />
                      <span className="text-sm text-gray-700">Outsourced Pharmacy</span>
                    </label>
                  </div>
                </div>
                {formData.pharmacyType === 'inhouse' && (
                  <div>{renderFormField('dlNumber', 'Drug License (DL) Number')}</div>
                )}
                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" name="hasNABH" checked={formData.hasNABH} onChange={handleInputChange} className="rounded border-green-300 text-green-500 focus:ring-green-500" />
                    <span className="text-green-700">NABH Certified</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" name="hasQualityCouncil" checked={formData.hasQualityCouncil} onChange={handleInputChange} className="rounded border-green-300 text-green-500 focus:ring-green-500" />
                    <span className="text-green-700">Quality Council of India</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
              <h4 className="font-semibold text-secondary-800 mb-2">Managing Director (MD)</h4>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField('mdName', 'MD Name')}
              {renderFormField('mdContact', 'MD Contact', 'tel')}
            </div>
            {renderFormField('mdEmail', 'MD Email', 'email', false)}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input type="checkbox" name="mdAsRepresentative" checked={formData.mdAsRepresentative} onChange={handleInputChange} className="rounded border-amber-300 text-amber-500 focus:ring-amber-500" />
                <div>
                  <span className="font-medium text-amber-800">MD will act as Hospital Representative</span>
                </div>
              </label>
            </div>
            {!formData.mdAsRepresentative && (
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  {renderFormField('representativeName', 'Representative Name')}
                  {renderFormField('designation', 'Designation')}
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {renderFormField('representativeEmail', 'Representative Email', 'email')}
                  {renderFormField('representativePhone', 'Representative Phone', 'tel')}
                </div>
              </>
            )}
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">Documents</h4>
                  <p className="text-sm text-amber-700 mt-1">Upload only the files you want to replace, or add more facility photos.</p>
                </div>
              </div>
            </div>
            <div className="grid gap-6">
              {documentConfig.map(renderDocumentUpload)}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">Account & Bank Details</h4>
            </div>
            {renderFormField('accountEmail', 'Account Email', 'email', true, '', { disabled: true })}
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField('bankName', 'Bank Name')}
              {renderFormField('accountHolderName', 'Account Holder Name')}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField('accountNumber', 'Account Number')}
              {renderFormField('ifscCode', 'IFSC Code')}
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-800">Review Changes</h4>
                  <p className="text-sm text-green-700 mt-1">Click Save to update the hospital details.</p>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2"><Building className="h-4 w-4" /><span className="font-medium">Hospital:</span><span>{formData.hospitalName}</span></div>
              <div className="flex items-center space-x-2"><Mail className="h-4 w-4" /><span className="font-medium">Email:</span><span>{formData.email}</span></div>
              <div className="flex items-center space-x-2"><Phone className="h-4 w-4" /><span className="font-medium">Phone:</span><span>{formData.phone}</span></div>
              <div className="flex items-center space-x-2"><MapPin className="h-4 w-4" /><span className="font-medium">Location:</span><span>{formData.city}, {formData.state}</span></div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading hospital data...</p>
        </div>
      </div>
    );
  }

  if (error && !docId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-2">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-3 rounded-xl">
              <Building className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Edit Hospital</h1>
              <p className="text-sm text-teal-600">Update registration details for this hospital</p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`flex items-center justify-center w-16 h-16 rounded-full border-2 transition-all duration-300 ${isActive ? 'bg-teal-500 border-teal-500 text-white shadow-lg' : isCompleted ? 'bg-gray-100 border-gray-300 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    <div className="mt-3 text-center max-w-24">
                      <p className={`text-sm font-medium leading-tight ${isActive ? 'text-teal-600' : 'text-gray-500'}`}>{step.title}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-0.5 w-16 mx-4 transition-colors duration-300 ${isCompleted ? 'bg-teal-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Card */}
        <div className="card-medical mb-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}
          {renderStep()}
        </div>

        {/* Nav buttons */}
        <div className="flex justify-between items-center">
          <button onClick={prevStep} disabled={currentStep === 1} className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'}`}>
            <ArrowLeft className="h-5 w-5" />
            <span>Previous</span>
          </button>
          <div className="text-sm text-gray-500 hidden md:block">Step {currentStep} of {steps.length}</div>
          {currentStep < 6 ? (
            <button onClick={nextStep} className="flex items-center space-x-2 btn-primary">
              <span>Next</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          ) : (
            <button onClick={saveChanges} disabled={saving} className="flex items-center space-x-2 btn-success disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? (<><Loader className="h-5 w-5 animate-spin" /><span>Saving...</span></>) : (<><CheckCircle className="h-5 w-5" /><span>Save Changes</span></>)}
            </button>
          )}
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <button onClick={() => navigate(`/admin/hospital-details/${applicationId}`)} className="inline-flex items-center text-teal-600 hover:text-teal-800">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalEdit;
